import React, { useEffect, useState } from "react";
import { connect, useDispatch, useSelector } from "react-redux";
import { ROOT } from "../../config/product-mgmt.route";
import { selectLoading } from "modules/notification";
import { unwrapResult } from "@reduxjs/toolkit";
import { AccessRights } from "lib/constants/accessRights";
import { setPageWorkspaceLanguage } from "modules/product-mgmt/redux/slice/productPage.slice";
import LoadingComponent from "components/loading";
import EditTemplateComponent from "./edit-template.page";
import { useHistory, useRouteMatch } from "react-router-dom";
import {
  editTemplate,
  fetchCustomTemplateHtml,
  fetchTemplate,
  fetchTemplateSettings,
} from "../../redux/action/template.action";
import { getConsumerPageSetting } from "../../../account/redux";
import {
  setCustomTemplateWorkspacePreviewState,
  setTemplateEditorMode,
} from "../../redux/slice/products.slice";
import { uploadJSONToS3 } from "../../../../app/feature";
import { UploadType } from "../../../../lib/constants/aws_s3";
import { getAllProducts } from "../../redux/action/products.action";
import { ProductPagePreviewDialog } from "modules/product-mgmt/components/dialog";
import { canAccess } from "../../../profile/redux/selector/profile.selector";

function EditTemplateContainer({
  hasViewAccessRight,
  hasEditAccessRight,
  lang,
  isFetching,
  fetchTemplate,
  getConsumerPageSetting,
  handleSave,
  workspaceSetting,
  template,
  customTemplateHtml,
  isPreviewingGenuine,
  isPreviewFetching,
}) {
  const history = useHistory();
  const match = useRouteMatch();
  const id = match.params.templateId;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const companyUuid = useSelector((state) => state.account.id);
  const productLanguages = useSelector((state) => state.productPage.lang);
  const currentLanguage = useSelector(
    (state) => state.productPage.pageWorkspaceLanguage
  );
  const productDropdown = useSelector((state) => state.products.allProducts);
  const isFetchingProduct = useSelector((state) =>
    selectLoading(state, getAllProducts.typePrefix)
  );
  const dispatch = useDispatch();
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isTemplateLoading, setTemplateLoading] = useState(false);

  const canEditName = useSelector((state) =>
    canAccess(state, AccessRights.Product.LANDING_TEMPLATE_UPDATE_NAME)
  );
  const canEditProduct = useSelector((state) =>
    canAccess(
      state,
      AccessRights.Product.LANDING_TEMPLATE_UPDATE_ASSIGNED_PRODUCTS
    )
  );
  const canEditComponent = useSelector((state) =>
    canAccess(state, AccessRights.Product.LANDING_TEMPLATE_UPDATE_COMPONENT)
  );
  const canEditSetting = useSelector((state) =>
    canAccess(state, AccessRights.Product.LANDING_TEMPLATE_UPDATE_SETTING)
  );

  const hasSurveyAccess = useSelector((state) =>
    canAccess(state, AccessRights.Survey.LISTING_READ)
  );
  const hasLuckyDrawAccess = useSelector((state) =>
    canAccess(state, AccessRights.Campaign.LUCKY_DRAW_READ)
  );
  const hasLuckyDrawV2Access = useSelector((state) =>
    canAccess(state, AccessRights.CampaignV2.LUCKY_DRAW_LIST_READ)
  );
  const hasInstantLuckyDrawAccess = useSelector((state) =>
    canAccess(state, AccessRights.Campaign.INSTANT_LUCKY_DRAW_READ)
  );
  const hasLoyaltyProgramAccess = useSelector((state) =>
    canAccess(state, AccessRights.LoyaltyProgram.LOYALTY_POINT_READ)
  );

  const hasCampaignAccess =
    hasSurveyAccess ||
    hasLuckyDrawAccess ||
    hasLuckyDrawV2Access ||
    hasInstantLuckyDrawAccess ||
    hasLoyaltyProgramAccess;

  const onSetLanguage = (language) => {
    dispatch(setPageWorkspaceLanguage(language));
  };

  // useEffect(() => {
  //   if (!hasViewAccessRight) {
  //     history.push('/admin/dashboard')
  //   }
  // }, [history, hasViewAccessRight])

  useEffect(() => {
    dispatch(getAllProducts());
  }, [dispatch]);

  useEffect(() => {
    if (id) {
      fetchTemplate(id)
        .then(unwrapResult)
        .catch(() => history.push(ROOT));
    } else {
      history.push(ROOT);
    }

    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getConsumerPageSetting();
    dispatch(setCustomTemplateWorkspacePreviewState(1));
  }, [getConsumerPageSetting, dispatch]);

  if (!hasViewAccessRight) return <LoadingComponent />;

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    dispatch(setTemplateEditorMode(false));

    if (template.isCustom) {
      handleSave({
        id,
        name: values.name,
        product_ids: values.products.map((e) => e.id),
        settings_json_url: template.url,
      }).then(() => {
        setIsSubmitting(false);
        dispatch(setTemplateEditorMode(true));
      });
      return;
    }

    await dispatch(
      uploadJSONToS3({
        uploadType: UploadType.productPageUiTemplate,
        data: workspaceSetting,
        id: companyUuid,
        fileName: `${Date.now()}-${values.name}.json`,
      })
    )
      .then(unwrapResult)
      .then((url) => {
        handleSave({
          id,
          name: values.name,
          product_ids: values.products.map((e) => e.id),
          settings_json_url: url,
        }).then(() => {
          setIsSubmitting(false);
          dispatch(setTemplateEditorMode(true));
        });
      });
  };

  const onPreviewGenuineChange = (event) => {
    setTemplateLoading(true);
    dispatch(setCustomTemplateWorkspacePreviewState(event.target.value));
    dispatch(
      fetchCustomTemplateHtml({
        template: template.url,
        isGenuine: event.target.value,
      })
    ).then(() => setTemplateLoading(false));
  };

  const handlePreview = () => {
    setIsPreviewDialogOpen(true);
  };

  return (
    <>
      <EditTemplateComponent
        hasEditAccessRight={hasEditAccessRight}
        canEditName={canEditName}
        canEditProduct={canEditProduct}
        canEditComponent={canEditComponent}
        canEditSetting={canEditSetting}
        currentLanguage={currentLanguage}
        productLanguages={productLanguages}
        isLoading={isFetching}
        lang={lang}
        onSetLanguage={onSetLanguage}
        handleSave={handleSubmit}
        template={template}
        isSubmitting={isSubmitting}
        productDropdown={productDropdown}
        isFetchingProduct={isFetchingProduct}
        customTemplateHtml={customTemplateHtml}
        isPreviewingGenuine={isPreviewingGenuine}
        onPreviewGenuineChange={onPreviewGenuineChange}
        isPreviewFetching={isPreviewFetching}
        handlePreview={handlePreview}
        isTemplateLoading={isTemplateLoading}
        hasCampaignAccess={hasCampaignAccess}
        hasSurveyAccess={hasSurveyAccess}
        hasLuckyDrawAccess={hasLuckyDrawAccess}
        hasLuckyDrawV2Access={hasLuckyDrawV2Access}
        hasInstantLuckyDrawAccess={hasInstantLuckyDrawAccess}
        hasLoyaltyProgramAccess={hasLoyaltyProgramAccess}
      />
      <ProductPagePreviewDialog
        isOpen={isPreviewDialogOpen}
        handleClose={() => setIsPreviewDialogOpen(false)}
        isEditTemplate={true}
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  hasViewAccessRight: canAccess(
    state,
    AccessRights.Product.LANDING_TEMPLATE_READ
  ),
  hasEditAccessRight: canAccess(
    state,
    AccessRights.Product.LANDING_TEMPLATE_UPDATE
  ),
  lang: state.constant.languages,
  isFetching:
    selectLoading(state, fetchTemplate.typePrefix) ||
    selectLoading(state, fetchTemplateSettings.typePrefix) ||
    selectLoading(state, getConsumerPageSetting.typePrefix) ||
    selectLoading(state, getAllProducts.typePrefix),
  template: state.products.template,
  workspaceSetting: state.products.template.workspaceSetting.setting,
  customTemplateHtml: state.products.template.workspaceSetting.customHtml,
  isPreviewingGenuine: state.products.template.workspaceSetting.isGenuine,
});

const mapDispatchToProps = (dispatch) => ({
  fetchTemplate: (id) => dispatch(fetchTemplate({ id })),
  getConsumerPageSetting: () => dispatch(getConsumerPageSetting()),
  handleSave: (payload) => dispatch(editTemplate(payload)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EditTemplateContainer);
